import { useEffect, useMemo, useState } from 'react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { useAuthStore } from '../services/authStore'
import legalAdminService from '../services/legalAdminService'
import LegalDocumentEditor from './LegalDocumentEditor'
import { useTranslation } from '../contexts/TranslationContext'

export default function LegalManagement() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [documents, setDocuments] = useState([])
  const [selectedDocumentId, setSelectedDocumentId] = useState('')
  const [versions, setVersions] = useState([])
  const [editorVersion, setEditorVersion] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [newDoc, setNewDoc] = useState({ type: 'terms_of_service', slug: '' })

  const renderVersionStatusLabel = (status) => {
    const normalized = String(status || '').trim().toLowerCase()
    if (normalized === 'draft') return t('legal.status.draft', 'draft')
    if (normalized === 'published') return t('legal.status.published', 'published')
    if (normalized === 'archived') return t('legal.status.archived', 'archived')
    return t('legal.status.unknown', normalized || 'unknown')
  }

  const selectedDocument = useMemo(
    () => documents.find((doc) => doc.id === selectedDocumentId) || null,
    [documents, selectedDocumentId]
  )

  const loadDocuments = async () => {
    setLoading(true)
    setError('')
    try {
      const items = await legalAdminService.listDocuments()
      setDocuments(items)
      if (items.length > 0 && !selectedDocumentId) {
        setSelectedDocumentId(items[0].id)
      }
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError.message || t('Failed to load legal documents', 'Failed to load legal documents'))
    } finally {
      setLoading(false)
    }
  }

  const loadVersions = async (documentId) => {
    if (!documentId) return
    try {
      const items = await legalAdminService.listVersions(documentId)
      setVersions(items)
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError.message || t('Failed to load document versions', 'Failed to load document versions'))
    }
  }

  useEffect(() => {
    loadDocuments()
  }, [])

  useEffect(() => {
    if (selectedDocumentId) {
      loadVersions(selectedDocumentId)
      setEditorVersion(null)
      setStats(null)
    }
  }, [selectedDocumentId])

  if (!user?.is_admin) {
    return <div className="text-sm text-red-600">{t('Admin access required.', 'Admin access required.')}</div>
  }

  const createDocument = async () => {
    if (!newDoc.slug.trim()) {
      setError(t('Slug is required.', 'Slug is required.'))
      return
    }
    setError('')
    await legalAdminService.createDocument({
      type: newDoc.type,
      slug: newDoc.slug.trim(),
      isActive: true,
    })
    setNewDoc((prev) => ({ ...prev, slug: '' }))
    await loadDocuments()
  }

  const openNewDraft = () => {
    setEditorVersion({
      version: '',
      locale: 'en',
      title: '',
      contentMarkdown: '',
      effectiveFrom: '',
      changeSummary: '',
      requiresReacceptance: false,
      status: 'draft',
      _isNew: true,
    })
  }

  const saveDraft = async (payload) => {
    if (!selectedDocumentId) return
    setSaving(true)
    setError('')
    try {
      if (editorVersion?._isNew) {
        await legalAdminService.createDraft(selectedDocumentId, payload)
      } else {
        await legalAdminService.updateDraft(editorVersion.id, payload)
      }
      await loadVersions(selectedDocumentId)
      setEditorVersion(null)
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError.message || t('Failed to save legal draft', 'Failed to save legal draft'))
    } finally {
      setSaving(false)
    }
  }

  const publish = async (versionId) => {
    await legalAdminService.publishVersion(versionId)
    await loadVersions(selectedDocumentId)
  }

  const archive = async (versionId) => {
    await legalAdminService.archiveVersion(versionId)
    await loadVersions(selectedDocumentId)
  }

  const openStats = async (versionId) => {
    const result = await legalAdminService.getAcceptanceStats(versionId)
    setStats(result)
  }

  return (
    <PageShell>
      <PageHeader title={t('Legal Management', 'Legal Management')} />
      {error ? <div className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</div> : null}

      <Card className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">{t('Create Legal Document', 'Create Legal Document')}</h2>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <select
            className="rounded-md border border-outline px-3 py-2 text-sm"
            value={newDoc.type}
            onChange={(e) => setNewDoc((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="terms_of_service">terms_of_service</option>
            <option value="privacy_policy">privacy_policy</option>
            <option value="ai_ocr_notice">ai_ocr_notice</option>
            <option value="cookie_policy">cookie_policy</option>
          </select>
          <Input placeholder={t('slug', 'slug')} value={newDoc.slug} onChange={(e) => setNewDoc((prev) => ({ ...prev, slug: e.target.value }))} />
          <Button onClick={createDocument}>{t('Create', 'Create')}</Button>
        </div>
      </Card>

      <Card className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">{t('Documents', 'Documents')}</h2>
          <Button onClick={openNewDraft} disabled={!selectedDocumentId}>{t('New Draft', 'New Draft')}</Button>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">{t('Loading...', 'Loading...')}</div>
        ) : (
          <select
            className="w-full rounded-md border border-outline px-3 py-2 text-sm"
            value={selectedDocumentId}
            onChange={(e) => setSelectedDocumentId(e.target.value)}
          >
            {documents.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.type} ({doc.slug})
              </option>
            ))}
          </select>
        )}
        {selectedDocument ? (
          <div className="space-y-2">
            {versions.map((version) => {
              const isPublished = version.status === 'published'
              return (
                <div key={version.id} className="rounded-md border border-outline p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{version.title || t('(untitled)', '(untitled)')}</p>
                      <p className="text-xs text-muted-foreground">v{version.version} - {renderVersionStatusLabel(version.status)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setEditorVersion(version)}>{t('Edit', 'Edit')}</Button>
                      {!isPublished ? <Button size="sm" onClick={() => publish(version.id)}>{t('Publish', 'Publish')}</Button> : null}
                      {!isPublished ? <Button variant="secondary" size="sm" onClick={() => archive(version.id)}>{t('Archive', 'Archive')}</Button> : null}
                      <Button variant="secondary" size="sm" onClick={() => openStats(version.id)}>{t('Stats', 'Stats')}</Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
      </Card>

      {editorVersion ? (
        <LegalDocumentEditor
          value={editorVersion}
          readOnly={editorVersion.status === 'published' && !editorVersion._isNew}
          onSave={saveDraft}
          onCancel={() => setEditorVersion(null)}
          saving={saving}
        />
      ) : null}

      {stats ? (
        <Card>
          <h3 className="text-base font-semibold text-foreground mb-2">{t('Acceptance Stats', 'Acceptance Stats')}</h3>
          <p className="text-sm text-foreground">{t('Version ID', 'Version ID')}: {stats.documentVersionId}</p>
          <p className="text-sm text-foreground">{t('Total acceptances', 'Total acceptances')}: {stats.totalAcceptances}</p>
          <p className="text-sm text-foreground">{t('Unique users', 'Unique users')}: {stats.uniqueUsers}</p>
        </Card>
      ) : null}
    </PageShell>
  )
}

